'use client'

import { Search, X } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AreaType } from '@/types/area-type'
import type { CourseType } from '@/types/course-type'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { bff_client } from '@/lib/apiClient'
import { SearchResults } from './search-results'

interface MobileSearchButtonProps {
  areas: AreaType[]
}

const SEARCH_DELAY_MS = 300

export function MobileSearchButton({ areas }: MobileSearchButtonProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<CourseType[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreResults, setHasMoreResults] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const params = new URLSearchParams(searchParams.toString())

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value)
      setCurrentPage(1)
      setSearchResults([])
      setHasMoreResults(false)
      params.set('area', event.target.value)
      router.push(`?${params.toString()}`, { scroll: false })
    },
    [params, router]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        if (searchQuery.trim()) {
          router.push(`/cursos?q=${encodeURIComponent(searchQuery.trim())}`)
          setOpen(false)
        }
      }
    },
    [searchQuery, router]
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setCurrentPage(1)
    setSearchResults([])
    setHasMoreResults(false)
    params.delete('area')
    router.push(`?${params.toString()}`, { scroll: false })
  }, [params, router])

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setSearchQuery(suggestion)
      setCurrentPage(1)
      setSearchResults([])
      setHasMoreResults(false)
      const selectedArea = areas.find(area => area.name === suggestion)
      if (selectedArea) {
        params.set('area', selectedArea.url)
        router.push(`?${params.toString()}`, { scroll: false })
      }
    },
    [areas, params, router]
  )

  const loadMoreResults = useCallback(async () => {
    if (isLoadingMore || !hasMoreResults) {
      return
    }

    setIsLoadingMore(true)
    try {
      const selectedArea = areas.find(area => area.name === searchQuery)
      const nextPage = currentPage + 1

      const queryParams: Record<string, string | number> = {
        per_page: 6,
        page: nextPage,
      }

      if (selectedArea) {
        queryParams['areas[]'] = selectedArea.url
      } else {
        queryParams.search = searchQuery
      }

      const response = await bff_client.get('products', {
        searchParams: queryParams,
      })
      const data = (await response.json()) as {
        data: CourseType[]
        pagination: { total_pages: number }
      }

      setSearchResults(prev => [...prev, ...(data.data || [])])
      setCurrentPage(nextPage)
      setHasMoreResults(nextPage < data.pagination.total_pages)
    } catch {
      // Silently fail for load more
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, hasMoreResults, areas, searchQuery, currentPage])

  const searchCourses = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      setHasMoreResults(false)
      return
    }

    setIsSearching(true)
    try {
      const selectedArea = areas.find(area => area.name === searchQuery)

      const queryParams: Record<string, string | number> = {
        per_page: 6,
        page: 1,
      }

      if (selectedArea) {
        queryParams['areas[]'] = selectedArea.url
      } else {
        queryParams.search = searchQuery
      }

      const response = await bff_client.get('products', {
        searchParams: {
          ...queryParams,
          status: 'actived',
        },
      })
      const data = (await response.json()) as {
        data: CourseType[]
        pagination: { total_pages: number }
      }
      setSearchResults(data.data || [])
      setCurrentPage(1)
      setHasMoreResults(data.pagination.total_pages > 1)
    } catch {
      setSearchResults([])
      setHasMoreResults(false)
    } finally {
      setIsSearching(false)
    }
  }, [searchQuery, areas])

  const quickSuggestions = useMemo(() => areas.map(area => area.name), [areas])

  useEffect(() => {
    const timeoutId = setTimeout(searchCourses, SEARCH_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }, [searchCourses])

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button
          aria-label="Abrir busca de cursos"
          className="lg:hidden"
          size="icon"
          variant="outline"
        >
          <Search className="size-5" />
        </Button>
      </SheetTrigger>

      <SheetContent className="h-auto" side="top">
        <SheetHeader className="space-y-4">
          <SheetClose asChild>
            <Button
              aria-label="Fechar busca de cursos"
              className="lg:hidden"
              size="icon"
              variant="outline"
            >
              <X className="size-5" />
            </Button>
          </SheetClose>

          <SheetTitle className="text-center font-semibold text-lg text-primary-900">
            Buscar Cursos
          </SheetTitle>
          <SheetDescription className="text-center text-muted-foreground text-sm">
            Encontre o curso ideal para sua carreira
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="relative">
            <Search className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-muted-foreground" />
            <Input
              autoFocus
              className="pr-10 pl-10"
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder="Digite para buscar cursos..."
              value={searchQuery}
            />
            {searchQuery && (
              <Button
                aria-label="Limpar busca"
                className="-translate-y-1/2 absolute top-1/2 right-1 size-8"
                onClick={handleClearSearch}
                size="icon"
                variant="ghost"
              >
                <X className="size-4" />
              </Button>
            )}
          </div>

          <SearchResults
            hasMoreResults={hasMoreResults}
            isLoading={isSearching}
            isLoadingMore={isLoadingMore}
            onClose={() => setOpen(false)}
            onLoadMore={loadMoreResults}
            results={searchResults}
            searchQuery={searchQuery}
          />

          {!searchQuery && (
            <div className="space-y-3">
              <h3 className="font-medium text-muted-foreground text-sm">
                Busca rápida
              </h3>
              <div className="flex flex-wrap gap-2">
                {quickSuggestions.map(suggestion => (
                  <Button
                    className="h-8 rounded-full px-3 text-xs"
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    variant="secondary"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
